import { PspRules } from "@CommonTypes/backend/PspRules";

type WeightRules = NonNullable<PspRules["weight_rules"]>;

/**
 * The weight rules as a config file carries them: everything except `report`.
 *
 * `report` is the backend's to set — owa-backend's `buildWeightRules`
 * hardcodes `report: true` and never reads the value we send — so writing our
 * own into the file would let an import/export round trip silently rewrite
 * whatever the user supplied. It still goes out on the generation payload,
 * where the contract requires the field.
 */
type WeightRulesInFile = Omit<WeightRules, "report">;

export type PspRulesFromFile = Omit<PspRules, "weight_rules"> & {
  weight_rules?: WeightRulesInFile;
};

/** What arrives at either boundary: our own rules, or an older file's. */
type PspRulesWithOptionalReport = Omit<PspRules, "weight_rules"> & {
  weight_rules?: WeightRulesInFile & { report?: boolean };
};

interface PspRulesTemplate {
  _version: "1";
  rules: PspRulesFromFile;
}

const OBJECT_SECTIONS = new Set([
  "fixture",
  "range",
  "merchandising",
  "high_density_mode",
  "high_density_building_blocks_mode",
  "relax_factors",
]);

/**
 * `weight_rules` is absent by design: requiring it would reject configs
 * exported before it existed, and configs from merchants with the gate off.
 * It still round-trips; an import without it falls back to defaults.
 */
const REQUIRED_RULE_SECTIONS = [
  "fixture",
  "range",
  "merchandising",
  "high_density_mode",
  "high_density_building_blocks_mode",
  "mudmap_level",
  "optimize_on",
  "relax_factors",
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

const HEAVY_BRAND_BLOCK_LEVELS = new Set(["cdt1", "cdt2", "cdt3"]);

// Absent is fine (see REQUIRED_RULE_SECTIONS comment); present, the section
// must be the whole shape, since a partial one is malformed by contract.
const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

function isValidWeightRules(value: unknown): value is WeightRulesInFile {
  if (!isRecord(value)) return false;

  if (
    value.default_shelf_weight_limit !== null &&
    !isFiniteNumber(value.default_shelf_weight_limit)
  )
    return false;

  if (value.heavy_rule !== null) {
    if (!isRecord(value.heavy_rule)) return false;
    if (!isFiniteNumber(value.heavy_rule.item_threshold)) return false;
    if (!isFiniteNumber(value.heavy_rule.max_deck_y)) return false;
  }

  if (typeof value.resolve_overweight_shelves !== "boolean") return false;
  if (typeof value.swap_heavy_brands_below_max_height !== "boolean") return false;
  if (
    typeof value.heavy_brand_block_level !== "string" ||
    !HEAVY_BRAND_BLOCK_LEVELS.has(value.heavy_brand_block_level)
  )
    return false;
  if (
    !isFiniteNumber(value.swap_fill_floor_pct) ||
    value.swap_fill_floor_pct < 0 ||
    value.swap_fill_floor_pct > 1
  )
    return false;

  return true;
}

function isValidPspRulesTemplate(data: unknown): data is PspRulesTemplate {
  if (!isRecord(data)) return false;
  if (data._version !== "1") return false;
  if (!isRecord(data.rules)) return false;
  const rules = data.rules;
  if (
    !REQUIRED_RULE_SECTIONS.every((key) =>
      OBJECT_SECTIONS.has(key) ? isRecord(rules[key]) : typeof rules[key] === "string",
    )
  )
    return false;
  return rules.weight_rules === undefined || isValidWeightRules(rules.weight_rules);
}

/** Both boundaries go through here, so no config file can carry `report`. */
function withoutReport(rules: PspRulesWithOptionalReport): PspRulesFromFile {
  if (!rules.weight_rules) {
    const { weight_rules: _absent, ...withNoWeightRules } = rules;
    return withNoWeightRules;
  }

  const { report: _report, ...weightRules } = rules.weight_rules;
  return { ...rules, weight_rules: weightRules };
}

export function exportPspRulesConfig(rules: PspRules): string {
  const template: PspRulesTemplate = { _version: "1", rules: withoutReport(rules) };
  return JSON.stringify(template, null, 2);
}

export async function parsePspRulesTemplate(file: File): Promise<PspRulesFromFile> {
  const text = await file.text();
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error("File is not valid JSON");
  }
  if (!isValidPspRulesTemplate(parsed)) {
    throw new Error("File does not contain a valid planogram configuration");
  }
  return withoutReport(parsed.rules);
}
